import { createAsyncThunk } from "@reduxjs/toolkit";
import { AccountApi, Configuration, GetProfileResponse, UserApi } from "api";
import { getBasePath } from "common/getConfiguration";
import { createErrorObject } from "errors/createErrorObject";
import { ErrorObject } from "errors/errorObject";
import { closeSnackbar, openSnackbar } from "features/ui";
import { getTranslationID } from "translation";
import type { RootState } from "app/store";
import axios from "axios";
import { PostalCodeResponse } from "./types";

export const initialAsync = createAsyncThunk<
  GetProfileResponse,
  { isCloseSnackbar: boolean },
  {
    state: RootState;
    // rejectした時の返却値の型
    rejectValue: {
      error: ErrorObject;
    };
  }
>("profile/initialAsync", async (args, thunkApi) => {
  const { isCloseSnackbar } = args;

  // apiのConfigurationを取得する
  const basePath = getBasePath();

  const config = new Configuration({ basePath });

  try {
    if (isCloseSnackbar) {
      thunkApi.dispatch(closeSnackbar());
    }
    const userApi = new UserApi(config);
    const { data } = await userApi.getProfile();

    return data;
  } catch (e) {
    const error = createErrorObject(e);
    thunkApi.dispatch(
      openSnackbar({
        level: "error",
        message: getTranslationID("common.message.serverInternalError"),
      })
    );
    return thunkApi.rejectWithValue({ error });
  }
});

export const updateAsync = createAsyncThunk<
  { success: boolean },
  void,
  {
    state: RootState;
    // rejectした時の返却値の型
    rejectValue: {
      error: ErrorObject;
    };
  }
>("profile/updateAsync", async (args, thunkApi) => {
  // apiのConfigurationを取得する
  const basePath = getBasePath();

  const {
    country,
    familyName,
    familyNameFurigana,
    firstName: givenName,
    firstNameFurigana: givenNameFurigana,
    address,
    company,
    companyFurigana,
    buildingName,
    city,
    companyTel,
    department,
    jobType,
    office,
    position,
    postalCode,
    state,
    market_status,
  } = thunkApi.getState().profile.apps;
  const config = new Configuration({ basePath });

  try {
    thunkApi.dispatch(closeSnackbar());
    const userApi = new UserApi(config);
    await userApi.putProfile({
      country,
      familyName,
      familyNameFurigana,
      givenName,
      givenNameFurigana,
      jobType,
      position,
      office,
      address,
      company,
      companyFurigana,
      department,
      companyTel,
      postalCode,
      state,
      city,
      buildingName,
      market_status,
    });
    thunkApi.dispatch(
      openSnackbar({
        level: "success",
        message: getTranslationID("profilePage.message.changeProfileSuccess"),
      })
    );
    return { success: true };
  } catch (e) {
    const error = createErrorObject(e);
    if (error.errorCode === "E000601") {
      thunkApi.dispatch(
        openSnackbar({
          level: "error",
          message: getTranslationID("profilePage.message.emailError"),
        })
      );
      return thunkApi.rejectWithValue({ error });
    }
    thunkApi.dispatch(
      openSnackbar({
        level: "error",
        message: getTranslationID("common.message.serverInternalError"),
      })
    );
    return thunkApi.rejectWithValue({ error });
  }
});

export const changePasswordAsync = createAsyncThunk<
  { success: boolean },
  void,
  {
    state: RootState;
    // rejectした時の返却値の型
    rejectValue: {
      error: ErrorObject;
    };
  }
>("profile/changePasswordAsync", async (args, thunkApi) => {
  // apiのConfigurationを取得する
  const basePath = getBasePath();

  const config = new Configuration({ basePath });
  const { currentPassword, newPassword } = thunkApi.getState().profile.apps;
  try {
    thunkApi.dispatch(closeSnackbar());
    // パスワード変更API
    const accountApi = new AccountApi(config);
    await accountApi.password({ currentPassword, newPassword });
    thunkApi.dispatch(
      openSnackbar({
        level: "success",
        message: getTranslationID("profilePage.message.changePasswordSuccess"),
      })
    );
    return { success: true };
  } catch (e) {
    const error = createErrorObject(e);
    // 現在のパスワードが一致しない場合
    if (error.errorCode === "E000201") {
      thunkApi.dispatch(
        openSnackbar({
          level: "error",
          message: getTranslationID(
            "profilePage.message.notMatchCurrentPassswordError"
          ),
        })
      );
      return thunkApi.rejectWithValue({ error });
    }
    // Cognito NG
    if (error.errorCode === "E000203") {
      thunkApi.dispatch(
        openSnackbar({
          level: "error",
          message: getTranslationID(
            "profilePage.message.changePasswordErrorRequiringRetry"
          ),
        })
      );
      return thunkApi.rejectWithValue({ error });
    }
    // Sentinel NG
    if (error.errorCode === "E000105") {
      thunkApi.dispatch(
        openSnackbar({
          level: "error",
          message: getTranslationID(
            "profilePage.message.changePasswordErrorRequiringRetry"
          ),
        })
      );
      return thunkApi.rejectWithValue({ error });
    }

    thunkApi.dispatch(
      openSnackbar({
        level: "error",
        message: getTranslationID("common.message.serverInternalError"),
      })
    );
    return thunkApi.rejectWithValue({ error });
  }
});

export const postalCodeAsync = createAsyncThunk<
  PostalCodeResponse,
  void,
  {
    state: RootState;
    // rejectした時の返却値の型
    rejectValue: {
      error: ErrorObject;
    };
  }
>("profile/postalCodeAsync", async (args, thunkApi) => {
  // apiのConfigurationを取得する
  const url = "https://zipcloud.ibsnet.co.jp/api/search";

  const { postalCode } = thunkApi.getState().profile.apps;

  const urlWithParam = `${url}?zipcode=${postalCode}`;

  try {
    // 郵便番号から住所を取得するAPI呼び出し
    const { data } = await axios.get(urlWithParam);
    return data;
  } catch (e) {
    const error = createErrorObject(e);
    return thunkApi.rejectWithValue({ error });
  }
});
